import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type Deal, type ProblemPool, dealBoard } from '../core/deal';
import {
  type RulesVariantConfig,
  roundSeconds,
} from '../core/rulesVariantConfig';
import type { GridConfig } from '../core/types';

const MS_PER_SECOND = 1000;

/**
 * How long the board stays blank between two layers of the same game.
 *
 * Legacy `runAnimationBody` wipes the blocker across the board over
 * `animMs * 2` (300ms), then `resetAnimation` slides it back over `animMs * 5`
 * (750ms) before `startNewQuadrant` deals the next board - so the gap between
 * clearing a board and the next one appearing is ~1050ms
 * (`games/VIFRulesAddSub.ts:713-728`, `games/engine/rulesBoardEngine.ts:469-492`).
 * `VIFRulesMulDiv.runAnimationBody` tweens for the same `150 * 2` = 300ms
 * before calling the same shared `resetAnimation` - identical total, so this
 * constant is shared rather than duplicated per family.
 */
export const LAYER_TRANSITION_MS = 1050;

/**
 * End-screen copy. Unlike the rules panel these really are strings in the
 * composition (`cjs.Text` fields on `endScreen`), so they are ports rather
 * than recoveries: `GameFrame.setStickersMessage` picks both lines at random
 * for a winning score, and `GameFrame.endGame` overrides the headline on the
 * last game of a round (`framework/GameFrame.ts:339-373`, `:419-459`).
 *
 * Only the winning branch is reachable here: `boardGameEngine.gameOver` sets
 * `score = scoreMax` before ending the game, so these boards never post a
 * losing score.
 */
const WIN_PRAISE = [
  'Fantastic!',
  'Outstanding!',
  'Great job!',
  'Excellent!',
  'Well done!',
  'Nice Work!',
] as const;

const WIN_DETAIL = ['You won!', 'You did it!', 'You got it right!'] as const;

const ROUND_COMPLETE_MESSAGE = 'You finished this round!';

/**
 * Where the module currently is in the legacy lifecycle:
 *
 * - `practice` - the practice menu is up, waiting for a round to be picked.
 *   Only reachable for preview logins (`GameFrame.clickChangeRounds`).
 * - `ready` - board blank, START enabled. Entered after a game starts and
 *   again after a timeout (`rulesBoardEngine.timeOutReset`).
 * - `playing` - a board is dealt and the clock, if the round has one, runs.
 * - `clearing` - board cleared, the layer-change wipe is running.
 * - `gameOver` - the end screen is up (`GameFrame.endGame`).
 */
export type RulesPhase =
  | 'practice'
  | 'ready'
  | 'playing'
  | 'clearing'
  | 'gameOver';

/** The two end-screen lines, fixed once when a game is won. */
export interface RulesEndScreen {
  /** Legacy `endScreen.messageText`. */
  message: string;
  /** Legacy `endScreen.afterScoreText`. */
  detail: string;
}

/**
 * Arguments passed to a family's `populateProblems` function - the shape
 * `populateRulesAddSubProblems`/`populateRulesMulDivProblems` both already
 * implement.
 */
export interface PopulateProblemsArgs<TFlag, TState> {
  flag: TFlag;
  layer: number;
  round: number;
  columns: number;
  state: TState;
  rng?: () => number;
}

/**
 * Everything a board needs that is not a family's own config and problem
 * plumbing - which is exactly what each family's game hook (`useAddSubGame`,
 * `useMulDivGame`) takes, and what `useVifRulesModule` hands to whichever of
 * them it was given.
 */
export interface UseRulesFamilyGameOptions<TFlag> {
  flag: TFlag;
  /** Board shape: rows and columns of the grid being dealt. */
  grid: GridConfig;
  /**
   * The player's position, owned by the caller.
   *
   * These are **not** local counters. `POST /Player/UpdateProgress` rejects any
   * round or game that is not the one the server last reported as next, so the
   * server owns them and this hook only reads them (see `useVifProgress`). In
   * practice mode the caller counts locally instead, and posts nothing.
   */
  round: number;
  game: number;
  /** Practice mode: the practice menu is reachable and nothing is scored. */
  practice?: boolean;
  /**
   * The caller has not resolved the position yet, so `round`, `game` and
   * `practice` are placeholders rather than answers, and gameplay stays
   * uninitialized until this goes false.
   */
  loading?: boolean;
  /** A game was won. The caller records it. Legacy `GameFrame.endGame`. */
  onGameWon?: () => void;
  /** The clock ran out. Legacy `timeOut`. */
  onTimeout?: () => void;
  /** The end screen's Next was pressed. The caller advances the position. */
  onNextGame?: () => void;
  /** A practice-menu round was picked. The caller moves the position to it. */
  onChooseRound?: (round: number) => void;
  /** Called once, from the end screen's Continue button on the final game. */
  onComplete: () => void;
  /** Injectable for deterministic tests. Defaults to `Math.random`. */
  rng?: () => number;
}

export interface UseRulesBoardGameOptions<
  TFlag,
  TState,
> extends UseRulesFamilyGameOptions<TFlag> {
  /** Resolves a flag to its pacing config. `addSubVariantConfig`/`mulDivVariantConfig`. */
  resolveConfig: (flag: TFlag) => RulesVariantConfig;
  /**
   * Creates a fresh problem-selection working state. Called again at every new
   * round or game with the outgoing state, so a family can carry over what its
   * legacy engine kept on the instance rather than per game.
   */
  createProblemState: (previous?: TState) => TState;
  /** Advances the working state to a layer/round's pool. */
  populateProblems: (args: PopulateProblemsArgs<TFlag, TState>) => ProblemPool;
}

export interface RulesGame {
  config: RulesVariantConfig;
  phase: RulesPhase;
  /** Legacy `gameFrame.round`, 1-based. */
  round: number;
  /** Legacy `gameFrame.game`, 1-based. */
  game: number;
  /** Legacy `intLayer`, 1-based. Drives the side game meter and the scoreboard. */
  layer: number;
  /** The board currently in play. A new object means "deal this". */
  deal: Deal;
  secondsLeft: number | null;
  /** Last game of the last round: the end screen offers Continue, not Next. */
  finalGame: boolean;
  /** Populated while `phase === 'gameOver'`. */
  endScreen: RulesEndScreen | null;
  /** Legacy `activateStartButton`. */
  start: () => void;
  /** Every cell resolved. Legacy `checkForEndBody`. */
  boardCleared: () => void;
  /** The wrong-answer fade finished. Legacy `resetGame`. */
  rejectSettled: () => void;
  /** End screen's Next button. Legacy `GameFrame.nextGame` in practice mode. */
  nextGame: () => void;
  /** End screen's Continue button. Legacy `continueVIF`. */
  continueModule: () => void;
  /** Practice menu round button. Legacy `GameFrame.clickRoundBtn`. */
  chooseRound: (round: number) => void;
  /** Legacy `changeRoundBtn`. */
  openPracticeMenu: () => void;
  /** Freezes the countdown for the rest of this board. Legacy `stopClockTimer`. */
  stopClock: () => void;
}

/**
 * Progression, pacing and problem selection for a "3xN rules" VIF variant -
 * generic over which family (add/sub, mul/div) supplies the config/problem
 * plumbing.
 *
 * Ports the parts of `GameFrame` plus `RulesBoardEngine` that the board
 * depends on - layers within a game, games within a round, rounds within the
 * module, the per-board countdown, and the end screen - without the
 * canvas-coupled scoreboard symbol and progress posting.
 *
 * The unit of work is a **board**, and `beginBoard` is the single path to one,
 * mirroring legacy `nextProblem()`: it re-runs `populateProblem`, records the
 * drawn indices in `blnChosen`, deals, and restarts the clock. This hook
 * reaches it from START, from a cleared board whose game has layers left, and
 * from the reset after a wrong answer on an untimed round of a variant that
 * does not return to START. A timeout deliberately is **not** one of them:
 * `timeOutReset` only re-enables START and leaves the next board to the
 * player. Neither is a wrong answer on a timed round, which does the same.
 */
export function useRulesBoardGame<TFlag, TState>({
  flag,
  grid,
  round,
  game,
  practice = false,
  loading = false,
  onGameWon,
  onTimeout,
  onNextGame,
  onChooseRound,
  onComplete,
  rng = Math.random,
  resolveConfig,
  createProblemState,
  populateProblems,
}: UseRulesBoardGameOptions<TFlag, TState>): RulesGame {
  const config = useMemo(() => resolveConfig(flag), [resolveConfig, flag]);

  // Legacy `setupGame` starts a game at `intLayer = gameFrame.game`, which is
  // what makes the side game meter fill across the four games of a
  // four-games-per-round variant instead of sticking at one segment.
  const [layer, setLayer] = useState(game);
  const [phase, setPhase] = useState<RulesPhase>(
    practice ? 'practice' : 'ready',
  );
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [clockStopped, setClockStopped] = useState(false);
  const [endScreen, setEndScreen] = useState<RulesEndScreen | null>(null);

  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const stopClock = useCallback(() => {
    setClockStopped(true);
  }, []);

  // The problem pool is stateful across layers: `blnChosen` remembers which
  // problems each layer drew, and later layers carry forward the ones no
  // earlier layer used. It must survive re-renders, hence a ref. Seeded
  // through `useState`'s lazy initializer so it is built once, not on every
  // render.
  const [initialProblemState] = useState(() => createProblemState());
  const problemState = useRef<TState>(initialProblemState);

  // Held in refs so a caller that passes a fresh closure each render cannot
  // change `beginBoard`'s identity. `rng` is a dependency of the
  // layer-transition effect below, and re-running that effect restarts its
  // timer - with an unstable `rng` the next board would never be dealt.
  const rngRef = useRef(rng);
  rngRef.current = rng;
  const populateProblemsRef = useRef(populateProblems);
  populateProblemsRef.current = populateProblems;

  // The first board is drawn on mount so the grid has something to lay out.
  // It is never played: every phase but `playing` conceals the tiles, exactly
  // as the canvas build's `resetAllTiles(false)` blanks them before START, and
  // START draws again. Consuming layer 1's `blnFirstTimeLayer` flag here is
  // harmless - each variant's layer 1 branch either reloads its table
  // unconditionally or leaves an already-loaded pool in place.
  const [deal, setDeal] = useState<Deal>(() =>
    drawBoard({
      flag,
      layer: 1,
      round: 1,
      grid,
      state: problemState.current,
      rng,
      populateProblems,
    }),
  );

  /**
   * Legacy `nextProblem()`: repopulate the pool for this layer/round, draw a
   * board, and put the round's seconds back on the clock.
   */
  const beginBoard = useCallback(
    (nextLayer: number, nextRound: number) => {
      setDeal(
        drawBoard({
          flag,
          layer: nextLayer,
          round: nextRound,
          grid,
          state: problemState.current,
          rng: rngRef.current,
          populateProblems: populateProblemsRef.current,
        }),
      );
      setSecondsLeft(roundSeconds(config, nextRound));
      setClockStopped(false);
    },
    [flag, grid, config],
  );

  const start = useCallback(() => {
    // Legacy `activateStartButton` disables the button as its first act, so a
    // second press cannot land. Guarding here matters now that START is what
    // draws the board - without it a stray press would reshuffle a board
    // mid-solve.
    if (phase !== 'ready') {
      return;
    }

    setPhase('playing');
    beginBoard(layer, round);
  }, [phase, beginBoard, layer, round]);

  const rejectSettled = useCallback(() => {
    // A wrong answer costs the whole board. On a timed round the board blanks,
    // the clock stops and START shows again, so the next board never starts
    // the clock unasked. This deliberately departs from legacy, whose rules
    // boards redeal at once with a full clock (`onActivateAfterReset` ->
    // `nextProblem()`). An untimed round redeals at once, as legacy does,
    // unless the variant always returns to START
    // (`config.rejectReturnsToStart`).
    if (config.rejectReturnsToStart || roundSeconds(config, round) !== null) {
      setPhase('ready');
      return;
    }
    beginBoard(layer, round);
  }, [config, beginBoard, layer, round]);

  const boardCleared = useCallback(() => {
    // Legacy `checkForEndBody`: `runAnimation` increments the layer first, so
    // the game-won test is against the incremented value. Some variants win
    // on the first cleared board whatever the layer count says
    // (`config.endsAfterOneBoard`).
    const gameWon = config.endsAfterOneBoard || layer + 1 > config.layersInGame;

    if (!gameWon) {
      setPhase('clearing');
      return;
    }

    setEndScreen({
      message:
        game === config.gamesPerRound
          ? ROUND_COMPLETE_MESSAGE
          : pick(WIN_PRAISE, rngRef.current),
      detail: pick(WIN_DETAIL, rngRef.current),
    });
    setSecondsLeft(null);
    setPhase('gameOver');

    // Legacy `gameOver` -> `GameFrame.endGame` posts the completed game before
    // the end screen is shown, so the sticker count can be rendered on it.
    onGameWon?.();
  }, [config, layer, game, onGameWon]);

  // The layer-change wipe. Legacy runs it as a blocker tween whose completion
  // callback deals the next board; here the phase holds the board blank for
  // the same span and then deals.
  useEffect(() => {
    if (phase !== 'clearing') {
      return;
    }

    const timer = setTimeout(() => {
      const nextLayer = layer + 1;
      setLayer(nextLayer);
      beginBoard(nextLayer, round);
      setPhase('playing');
    }, LAYER_TRANSITION_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [phase, layer, round, beginBoard]);

  const finalGame =
    round === config.roundsInModule && game === config.gamesPerRound;

  // Initialize gameplay from the *resolved* position, then adopt each new one.
  //
  // Both halves are here because they are the same decision. In scored play a
  // new position has just been re-read from the server; in practice it was
  // counted locally; on the first pass it has only just arrived. Either way a
  // new round or game means a fresh pool (keeping whatever the family carries
  // over - see `createProblemState`), a layer chain restarted at the game
  // number, and a phase chosen from what the position actually says.
  //
  // Deciding the phase at mount instead is deciding it before the answer
  // exists: `practice` is false and the position is `1:1` for as long as
  // `useVifProgress` is still reading. A player who has finished earning would
  // land on a scored board rather than the practice chooser, and one whose
  // stored position falls outside this variant would land on a board dealt for
  // a round the variant does not have.
  const initialized = useRef(!loading);
  const adopted = useRef(`${round}:${game}`);
  useEffect(() => {
    if (loading) {
      return;
    }

    const key = `${round}:${game}`;
    const initializing = !initialized.current;
    if (!initializing && adopted.current === key) {
      return;
    }
    initialized.current = true;
    adopted.current = key;

    problemState.current = createProblemState(problemState.current);
    setLayer(game);
    setEndScreen(null);
    setSecondsLeft(null);

    // Practice opens on the chooser, and returns to it once the locally
    // counted position runs off the end of the module - `nextPracticePosition`
    // reports round 0 for that, which is not a round anyone can play. Every
    // other position is a real one, including the round the chooser just
    // picked.
    setPhase(practice && (initializing || round < 1) ? 'practice' : 'ready');
  }, [loading, practice, round, game]);

  const nextGame = useCallback(() => {
    onNextGame?.();
  }, [onNextGame]);

  const completedRef = useRef(false);

  const continueModule = useCallback(() => {
    if (completedRef.current) {
      return;
    }
    completedRef.current = true;
    onComplete();
  }, [onComplete]);

  const chooseRound = useCallback(
    (chosen: number) => {
      onChooseRound?.(chosen);
      setPhase('ready');
    },
    [onChooseRound],
  );

  const openPracticeMenu = useCallback(() => {
    setEndScreen(null);
    setSecondsLeft(null);
    setPhase('practice');
  }, []);

  // Whether this board runs a clock at all. Derived as a boolean so the
  // interval below is created once per board rather than rebuilt every tick.
  const timed = secondsLeft !== null;

  // Countdown. A single interval owns the whole board: chaining `setTimeout`s
  // through effect re-runs made each tick depend on a render landing first,
  // which stalled the clock after one second and let it drift. `deal` is a dep
  // so a new board restarts the interval rather than inheriting its phase.
  useEffect(() => {
    if (phase !== 'playing' || !timed || clockStopped) {
      return;
    }

    const interval = setInterval(() => {
      setSecondsLeft((value) =>
        value === null ? null : Math.max(value - 1, 0),
      );
    }, MS_PER_SECOND);

    return () => {
      clearInterval(interval);
    };
  }, [phase, timed, deal, clockStopped]);

  // Reaching zero blanks the board and returns to START, matching legacy
  // `timeOutReset`. The board is deliberately not re-dealt here - the player
  // presses START, and that is what draws the next one.
  useEffect(() => {
    if (phase === 'playing' && secondsLeft === 0) {
      setPhase('ready');
      onTimeoutRef.current?.();
    }
  }, [phase, secondsLeft]);

  return {
    config,
    phase,
    round,
    game,
    layer,
    deal,
    secondsLeft,
    finalGame,
    endScreen,
    start,
    boardCleared,
    rejectSettled,
    nextGame,
    continueModule,
    chooseRound,
    openPracticeMenu,
    stopClock,
  };
}

interface DrawArgs<TFlag, TState> {
  flag: TFlag;
  layer: number;
  round: number;
  grid: GridConfig;
  state: TState;
  rng: () => number;
  populateProblems: (args: PopulateProblemsArgs<TFlag, TState>) => ProblemPool;
}

/**
 * The pool-and-deal half of legacy `nextProblem()`: repopulate for this
 * layer/round, draw a board from the pool, and record which pool entries the
 * draw used.
 *
 * Recording is not bookkeeping for its own sake: the next layer's
 * carry-forward logic (where a family has one) reads it to decide what is
 * left over. A row left unrecorded makes it carry the *entire* pool forward,
 * so a later layer redraws problems an earlier one already asked.
 */
function drawBoard<TFlag, TState>({
  flag,
  layer,
  round,
  grid,
  state,
  rng,
  populateProblems,
}: DrawArgs<TFlag, TState>): Deal {
  const pool = populateProblems({
    flag,
    layer,
    round,
    columns: grid.cols,
    state,
    rng,
  });

  const dealt = dealBoard(pool, grid, rng);

  const chosenRow = (state as { blnChosen?: number[][] }).blnChosen?.[
    layer - 1
  ];
  if (chosenRow) {
    for (const index of dealt.chosenIndices) {
      chosenRow[index] = 1;
    }
  }

  return dealt;
}

function pick<T>(values: readonly T[], rng: () => number): T {
  return values[Math.floor(rng() * values.length)] ?? values[0]!;
}
